/**
 * Shared vocabulary for the human-in-the-loop interaction API
 * (`/v1/human/interactions`, the `@novu/human` CLI, `human_relay`,
 * and custom code agents `ctx.*` helpers).
 */
export var HumanInteractionKindEnum;
(function (HumanInteractionKindEnum) {
    HumanInteractionKindEnum["ASK"] = "ask";
    HumanInteractionKindEnum["APPROVE"] = "approve";
    HumanInteractionKindEnum["CHOOSE"] = "choose";
    HumanInteractionKindEnum["TELL"] = "tell";
})(HumanInteractionKindEnum || (HumanInteractionKindEnum = {}));
export var HumanInteractionStatusEnum;
(function (HumanInteractionStatusEnum) {
    /** Delivered (or delivering) and waiting on the human. */
    HumanInteractionStatusEnum["PENDING"] = "pending";
    /** `ask`/`choose` resolved with a reply or an option pick. */
    HumanInteractionStatusEnum["ANSWERED"] = "answered";
    /** `approve` resolved positively. */
    HumanInteractionStatusEnum["APPROVED"] = "approved";
    /** `approve` resolved negatively. */
    HumanInteractionStatusEnum["DENIED"] = "denied";
    /** TTL elapsed before the human responded. */
    HumanInteractionStatusEnum["EXPIRED"] = "expired";
    /** Cancelled by the caller before resolution. */
    HumanInteractionStatusEnum["CANCELED"] = "canceled";
    /** Terminal state for `tell` — nothing to wait on. */
    HumanInteractionStatusEnum["DELIVERED"] = "delivered";
})(HumanInteractionStatusEnum || (HumanInteractionStatusEnum = {}));
export const HUMAN_INTERACTION_TERMINAL_STATUSES = [
    HumanInteractionStatusEnum.ANSWERED,
    HumanInteractionStatusEnum.APPROVED,
    HumanInteractionStatusEnum.DENIED,
    HumanInteractionStatusEnum.EXPIRED,
    HumanInteractionStatusEnum.CANCELED,
    HumanInteractionStatusEnum.DELIVERED,
];
export function isHumanCardElement(card) {
    return (typeof card === 'object' && card !== null && !Array.isArray(card) && card.type === 'card');
}
export function isHumanCardElementContent(content) {
    return (typeof content === 'object' &&
        content !== null &&
        !Array.isArray(content) &&
        isHumanCardElement(content.card));
}
export function isHumanChromeContent(content) {
    if (typeof content !== 'object' || content === null || Array.isArray(content)) {
        return false;
    }
    const chrome = content.cardChrome;
    return typeof chrome === 'object' && chrome !== null && !Array.isArray(chrome);
}
/** Extra approve buttons after Approve / Deny (Always allow, author extras). */
export const HUMAN_INTERACTION_MAX_EXTRA_ACTIONS = 4;
export const HUMAN_INTERACTION_RESERVED_OPTION_IDS = ['approve', 'deny'];
export const HUMAN_TRUST_TOOL_OPTION_ID = 'trust-tool';
export const HUMAN_TRUST_SERVER_OPTION_ID = 'trust-server';
export function mintHumanOptions(inputs) {
    return inputs.map((input, index) => {
        if (typeof input === 'string') {
            return { id: `opt_${index + 1}`, label: input };
        }
        return { id: input.id, label: input.label };
    });
}
function chromeFromContent(content) {
    if (isHumanChromeContent(content)) {
        return content.cardChrome;
    }
    if (isHumanCardElementContent(content)) {
        const title = content.card.title?.trim();
        return title ? { title } : undefined;
    }
    return undefined;
}
export function resolveHumanInteractionCard(source) {
    const chrome = chromeFromContent(source.content);
    const title = chrome?.title?.trim() || '';
    const icon = chrome?.icon;
    const subtitle = chrome?.subtitle;
    const body = chrome?.body;
    const presentation = {
        title,
        ...(icon ? { icon } : {}),
        ...(subtitle ? { subtitle } : {}),
        ...(body ? { body } : {}),
    };
    if (source.kind === HumanInteractionKindEnum.CHOOSE) {
        const rawOptions = chrome && 'options' in chrome && chrome.options?.length ? chrome.options : [];
        return {
            ...presentation,
            options: mintHumanOptions(rawOptions),
        };
    }
    if (source.kind === HumanInteractionKindEnum.APPROVE) {
        const extraActions = chrome && 'extraActions' in chrome && chrome.extraActions?.length
            ? mintHumanOptions(chrome.extraActions)
            : undefined;
        const approveLabel = chrome && 'approveLabel' in chrome ? chrome.approveLabel : undefined;
        const denyLabel = chrome && 'denyLabel' in chrome ? chrome.denyLabel : undefined;
        return {
            ...presentation,
            ...(approveLabel ? { approveLabel } : {}),
            ...(denyLabel ? { denyLabel } : {}),
            ...(extraActions?.length ? { extraActions } : {}),
        };
    }
    return presentation;
}
export function humanInteractionCardTitle(source) {
    return resolveHumanInteractionCard(source).title;
}
export function humanInteractionChooseOptions(source) {
    const card = resolveHumanInteractionCard(source);
    return 'options' in card ? card.options : [];
}
export function humanInteractionApproveExtraActions(source) {
    const card = resolveHumanInteractionCard(source);
    return 'extraActions' in card && card.extraActions ? card.extraActions : [];
}
/** Hard cap on `to` recipient lists for `ctx.*` and `POST /v1/human/interactions`. */
export const HUMAN_INTERACTION_MAX_RECIPIENTS = 50;
/** `choose` must have at least two options and at most this many (chat button UIs truncate). */
export const HUMAN_INTERACTION_MAX_CHOOSE_OPTIONS = 10;
/**
 * Delivery channel preference on create. The API resolves the concrete
 * integration from the relay agent's linked integrations + the human's
 * endpoints — callers never pass an integration identifier.
 */
export var HumanChannelViaEnum;
(function (HumanChannelViaEnum) {
    HumanChannelViaEnum["TELEGRAM"] = "telegram";
    HumanChannelViaEnum["SLACK"] = "slack";
    HumanChannelViaEnum["EMAIL"] = "email";
})(HumanChannelViaEnum || (HumanChannelViaEnum = {}));
/** Default lifetime of a pending interaction. */
export const HUMAN_INTERACTION_DEFAULT_TTL_SECONDS = 24 * 60 * 60;
/**
 * Hard cap on `--ttl`. Card action tokens are cached for 5 days
 * (see AgentActionTokenService); buttons must not outlive their tokens.
 */
export const HUMAN_INTERACTION_MAX_TTL_SECONDS = 5 * 24 * 60 * 60;
//# sourceMappingURL=human-interaction.js.map